import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { HelpCircle, Calendar, Users, MousePointer, Check } from 'lucide-react';

interface EventOption {
  eventName: string;
  displayName?: string;
  description?: string;
  eventCount?: number;
  lastSeen?: string;
}

interface EventClarificationMessageProps {
  question: string;
  events: EventOption[];
  onEventsSelect?: (selectedEvents: string[], followUpQuery: string) => void;
  originalQuery?: string;
}

export function EventClarificationMessage({ 
  question, 
  events = [], 
  onEventsSelect,
  originalQuery = ''
}: EventClarificationMessageProps) {
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleEventToggle = (eventName: string) => {
    setSelectedEvents(prev => 
      prev.includes(eventName) 
        ? prev.filter(e => e !== eventName)
        : [...prev, eventName]
    );
  };

  const handleContinue = async () => {
    if (selectedEvents.length === 0) return;
    
    setIsProcessing(true);
    
    // Create a follow-up query that includes the selected events
    const eventList = selectedEvents.length === 1 
      ? `the "${selectedEvents[0]}" event`
      : `the following events: ${selectedEvents.map(e => `"${e}"`).join(', ')}`;
    
    const followUpQuery = `${originalQuery} for ${eventList}`;
    
    await onEventsSelect?.(selectedEvents, followUpQuery);
    setIsProcessing(false);
  };

  const getEventIcon = (eventName: string) => {
    const name = eventName.toLowerCase();
    if (name.includes('click') || name.includes('tap')) return <MousePointer className="w-4 h-4" />;
    if (name.includes('user') || name.includes('signup')) return <Users className="w-4 h-4" />;
    if (name.includes('view') || name.includes('page')) return <Calendar className="w-4 h-4" />;
    return <MousePointer className="w-4 h-4" />;
  };

  return (
    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700/50 rounded-lg p-4 mb-4">
      <div className="flex items-start space-x-3">
        <div className="flex-shrink-0">
          <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
        </div>
        
        <div className="flex-1">
          <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
            Which event are you asking about?
          </h4>
          
          <p className="text-sm text-blue-700 dark:text-blue-300 mb-4">
            {question}
          </p>
          
          {events && events.length > 0 && (
            <div>
              <p className="text-xs text-blue-600 dark:text-blue-400 mb-3 font-medium">
                Select the event(s) you're interested in:
              </p>
              
              <div className="space-y-2 mb-4">
                {events.map((event) => (
                  <div
                    key={event.eventName}
                    className={`
                      flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-all
                      ${selectedEvents.includes(event.eventName)
                        ? 'bg-blue-100 dark:bg-blue-800/30 border-blue-300 dark:border-blue-600 shadow-sm'
                        : 'bg-white dark:bg-gray-800/50 border-blue-200 dark:border-blue-700/50 hover:bg-blue-50 dark:hover:bg-blue-800/20 hover:border-blue-300 dark:hover:border-blue-600'
                      }
                    `}
                    onClick={() => handleEventToggle(event.eventName)}
                  >
                    <div className={`
                      w-5 h-5 rounded border-2 flex items-center justify-center
                      ${selectedEvents.includes(event.eventName)
                        ? 'bg-blue-600 dark:bg-blue-500 border-blue-600 dark:border-blue-500'
                        : 'border-blue-300 dark:border-blue-600'
                      }
                    `}>
                      {selectedEvents.includes(event.eventName) && (
                        <Check className="w-3 h-3 text-white" />
                      )}
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <div className="text-blue-600 dark:text-blue-400">
                          {getEventIcon(event.eventName)}
                        </div>
                        <span className="font-medium text-blue-900 dark:text-blue-100 text-sm">
                          {event.displayName || event.eventName}
                        </span>
                        {event.eventCount && (
                          <Badge variant="secondary" className="text-xs">
                            {event.eventCount.toLocaleString()} events
                          </Badge>
                        )}
                      </div>
                      
                      {event.description && (
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 ml-6">
                          {event.description}
                        </p>
                      )}
                      
                      {event.lastSeen && (
                        <p className="text-xs text-blue-500 dark:text-blue-400 mt-1 ml-6">
                          Last seen: {event.lastSeen}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="flex items-center justify-between">
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  {selectedEvents.length === 0 
                    ? 'Select at least one event to continue'
                    : `${selectedEvents.length} event${selectedEvents.length === 1 ? '' : 's'} selected`
                  }
                </p>
                
                <Button
                  onClick={handleContinue}
                  disabled={selectedEvents.length === 0 || isProcessing}
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 text-white text-sm px-4 py-2"
                >
                  {isProcessing ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    `Continue with ${selectedEvents.length || 0} event${selectedEvents.length === 1 ? '' : 's'}`
                  )}
                </Button>
              </div>
            </div>
          )}
          
          <div className="mt-4 pt-3 border-t border-blue-200 dark:border-blue-700/50">
            <p className="text-xs text-blue-600 dark:text-blue-400">
              💡 You can select multiple events to compare their performance
            </p>
          </div>
        </div>
      </div>
    </div>
  );
} 